import { useMemo, useRef, useState, type ComponentType } from 'react'
import { ChallengeService } from '@duck/api-definitions'
import {
  CheckCircle2,
  CircleDashed,
  CircleDot,
  CircleDotDashed,
  Flame,
  Gauge,
  Hourglass,
  Layers,
  Leaf,
  RotateCcw,
  Trophy,
} from 'lucide-react'
import type { TranslationKey } from '@/modules/i18n'
import { challengeDifficultyOrder, challengeDifficultyHue, timeCategoryOrder, timeCategoryDisplayMap, timeCategoryIconMap, timeCategoryHue } from '@/utils/challengeData.utils'
import { useLocalSettings } from '@/hooks/useLocalSettings'
import { useDebouncedEffect } from '@/hooks/useDebouncedEffect'
import { trackChallengeBrowse } from '@/modules/challengesAnalytics'

type AC = ChallengeService.AvailableChallenge
export type GroupKey = 'time' | 'difficulty' | 'status' | 'none'
// Sort is split into a mode (what to order by) and a direction (asc/desc),
// driven by separate controls in the filter rail.
export type SortMode = 'difficulty' | 'az'
export type SortDir = 'asc' | 'desc'

// Sentinel kept alongside the real challenge statuses in the status filter set.
// It never matches a challenge's status, so selecting it alone shows only the
// Assignments category; it also gates whether assignments are visible at all.
export const ASSIGNMENTS_STATUS = 'assignments'

// Icon shown in each group header's badge — varies by the active grouping axis.
export type GroupIcon = ComponentType<{ className?: string }>

const DIFFICULTY_GROUP_ICON: Record<ChallengeService.ChallengeDifficultyEnum, GroupIcon> = {
  [ChallengeService.ChallengeDifficultyEnum.Easy]: Leaf,
  [ChallengeService.ChallengeDifficultyEnum.Intermediate]: Gauge,
  [ChallengeService.ChallengeDifficultyEnum.Hard]: Flame,
  [ChallengeService.ChallengeDifficultyEnum.Expert]: Trophy,
}

const STATUS_GROUP_ICON: Record<ChallengeService.AvailableChallengeStatusEnum, GroupIcon> = {
  [ChallengeService.AvailableChallengeStatusEnum.Available]: CircleDashed,
  [ChallengeService.AvailableChallengeStatusEnum.Unbooted]: CircleDotDashed,
  [ChallengeService.AvailableChallengeStatusEnum.Ongoing]: CircleDot,
  [ChallengeService.AvailableChallengeStatusEnum.Cooldown]: Hourglass,
  [ChallengeService.AvailableChallengeStatusEnum.Aborted]: RotateCcw,
  [ChallengeService.AvailableChallengeStatusEnum.Finished]: CheckCircle2,
}

const DIFFICULTY_LABEL: Record<ChallengeService.ChallengeDifficultyEnum, TranslationKey> = {
  [ChallengeService.ChallengeDifficultyEnum.Easy]: 'tasks.difficulty.easy',
  [ChallengeService.ChallengeDifficultyEnum.Intermediate]: 'tasks.difficulty.intermediate',
  [ChallengeService.ChallengeDifficultyEnum.Hard]: 'tasks.difficulty.hard',
  [ChallengeService.ChallengeDifficultyEnum.Expert]: 'tasks.difficulty.expert',
}

const STATUS_GROUP_ORDER: ChallengeService.AvailableChallengeStatusEnum[] = [
  ChallengeService.AvailableChallengeStatusEnum.Ongoing,
  ChallengeService.AvailableChallengeStatusEnum.Unbooted,
  ChallengeService.AvailableChallengeStatusEnum.Available,
  ChallengeService.AvailableChallengeStatusEnum.Aborted,
  ChallengeService.AvailableChallengeStatusEnum.Cooldown,
  ChallengeService.AvailableChallengeStatusEnum.Finished,
]

// The category list for a grouping axis, independent of any data — shared by the
// real grid and the loading skeleton so both lay out the same way.
export interface GroupTemplate {
  key: string
  labelKey: TranslationKey
  icon: GroupIcon
  iconClassName?: string
}

export const groupTemplates = (group: GroupKey): GroupTemplate[] => {
  if (group === 'none') {
    return [{ key: 'all', labelKey: 'challenges.allChallenges', icon: Layers, iconClassName: undefined }]
  }
  if (group === 'difficulty') {
    return (Object.keys(challengeDifficultyOrder) as ChallengeService.ChallengeDifficultyEnum[]).map((d) => ({
      key: d, labelKey: DIFFICULTY_LABEL[d], icon: DIFFICULTY_GROUP_ICON[d], iconClassName: challengeDifficultyHue[d].text,
    }))
  }
  if (group === 'status') {
    return STATUS_GROUP_ORDER.map((s) => ({
      key: s, labelKey: `challenges.statusGroup.${s}` as TranslationKey, icon: STATUS_GROUP_ICON[s], iconClassName: undefined,
    }))
  }
  return timeCategoryOrder.map((tc) => ({
    key: tc, labelKey: timeCategoryDisplayMap[tc], icon: timeCategoryIconMap[tc] as GroupIcon, iconClassName: timeCategoryHue[tc].text,
  }))
}

const itemsForGroup = (group: GroupKey, key: string, sorted: AC[]): AC[] => {
  if (group === 'none') return sorted
  if (group === 'difficulty') return sorted.filter((c) => c.difficulty === key)
  if (group === 'status') return sorted.filter((c) => c.status === key)
  return sorted.filter((c) => c.timeCategory === key)
}

const identity = (c: AC): string => c.challengeId ?? c.taskId

const toggle = <T,>(set: Set<T>, value: T): Set<T> => {
  const next = new Set(set)
  if (next.has(value)) {
    next.delete(value)
  } else {
    next.add(value)
  }
  return next
}

export const useChallengeBrowser = (challenges: AC[]) => {
  const { getSetting, setSetting } = useLocalSettings()
  const [query, setQuery] = useState('')
  const [difficulties, setDifficulties] = useState<Set<string>>(new Set())
  const [weapons, setWeapons] = useState<Set<string>>(new Set())
  const [statuses, setStatuses] = useState<Set<string>>(new Set())
  const [times, setTimes] = useState<Set<string>>(new Set())
  // Grouping and sort persist across reloads so the page renders instantly into
  // the user's last layout (and the loading skeleton groups the same way).
  const [group, setGroupState] = useState<GroupKey>(() => getSetting('challengeGroup') ?? 'time')
  const [sortMode, setSortModeState] = useState<SortMode>(() => getSetting('challengeSortMode') ?? 'difficulty')
  const [sortDir, setSortDirState] = useState<SortDir>(() => getSetting('challengeSortDir') ?? 'asc')

  const setGroup = (g: GroupKey) => {
    if (g !== group) trackChallengeBrowse('challenges:group:change', { group: g })
    setGroupState(g)
    setSetting('challengeGroup', g)
  }
  const setSortMode = (m: SortMode) => {
    if (m !== sortMode) trackChallengeBrowse('challenges:sort:mode', { mode: m })
    setSortModeState(m)
    setSetting('challengeSortMode', m)
  }
  const toggleSortDir = () => setSortDirState((d) => {
    const next = d === 'asc' ? 'desc' : 'asc'
    trackChallengeBrowse('challenges:sort:direction', { direction: next })
    setSetting('challengeSortDir', next)
    return next
  })

  // Search is tracked on a debounce so we record committed queries, not every
  // keystroke. The first run is skipped so an empty initial query never fires.
  const searchTrackedRef = useRef(false)
  useDebouncedEffect(() => {
    if (!searchTrackedRef.current) {
      searchTrackedRef.current = true
      return
    }
    const trimmed = query.trim()
    trackChallengeBrowse('challenges:filter:search', {
      hasQuery: trimmed !== '',
      length: trimmed.length,
    })
  }, [query], 600)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return challenges.filter((c) => {
      if (q !== '' && !(c.name + c.teaser).toLowerCase().includes(q)) return false
      if (difficulties.size > 0 && !difficulties.has(c.difficulty)) return false
      if (weapons.size > 0 && !c.allowedWeapons.some((w) => weapons.has(w))) return false
      if (statuses.size > 0 && !statuses.has(c.status)) return false
      if (times.size > 0 && !times.has(c.timeCategory)) return false
      return true
    })
  }, [challenges, query, difficulties, weapons, statuses, times])

  const sorted = useMemo(() => {
    const copy = [...filtered]
    if (sortMode === 'difficulty') copy.sort((a, b) => challengeDifficultyOrder[a.difficulty] - challengeDifficultyOrder[b.difficulty])
    if (sortMode === 'az') copy.sort((a, b) => a.name.localeCompare(b.name))
    if (sortDir === 'desc') copy.reverse()
    return copy
  }, [filtered, sortMode, sortDir])

  const groups = useMemo(
    () => groupTemplates(group)
      .map((tpl) => ({ ...tpl, items: itemsForGroup(group, tpl.key, sorted) }))
      .filter((g) => g.items.length > 0),
    [sorted, group]
  )

  const visibleOrder = useMemo(() => groups.flatMap((g) => g.items.map(identity)), [groups])

  const facetCounts = useMemo(() => {
    const count = (pred: (c: AC) => boolean) => challenges.filter(pred).length
    return {
      difficulty: Object.fromEntries((Object.keys(challengeDifficultyOrder) as ChallengeService.ChallengeDifficultyEnum[])
        .map((d) => [d, count((c) => c.difficulty === d)])),
      weapon: { SQL: count((c) => c.allowedWeapons.includes('SQL' as ChallengeService.TaskWeaponsOfChoiceEnum)),
                Python: count((c) => c.allowedWeapons.includes('Python' as ChallengeService.TaskWeaponsOfChoiceEnum)) },
      time: Object.fromEntries(timeCategoryOrder.map((tc) => [tc, count((c) => c.timeCategory === tc)])),
    }
  }, [challenges])

  const activeFilterCount = difficulties.size + weapons.size + statuses.size + times.size + (query.trim() === '' ? 0 : 1)

  // Assignments show when no status filter is set, or when the assignments
  // sentinel is explicitly selected (additive with the challenge statuses).
  const showAssignments = statuses.size === 0 || statuses.has(ASSIGNMENTS_STATUS)

  return {
    state: { query, difficulties, weapons, statuses, times, group, sortMode, sortDir },
    showAssignments,
    setQuery,
    toggleDifficulty: (v: string) => {
      trackChallengeBrowse('challenges:filter:toggle', { facet: 'difficulty', value: v, active: !difficulties.has(v) })
      setDifficulties((s) => toggle(s, v))
    },
    toggleWeapon: (v: string) => {
      trackChallengeBrowse('challenges:filter:toggle', { facet: 'language', value: v, active: !weapons.has(v) })
      setWeapons((s) => toggle(s, v))
    },
    toggleStatus: (v: string) => {
      trackChallengeBrowse('challenges:filter:toggle', { facet: 'status', value: v, active: !statuses.has(v) })
      setStatuses((s) => toggle(s, v))
    },
    toggleTime: (v: string) => {
      trackChallengeBrowse('challenges:filter:toggle', { facet: 'time', value: v, active: !times.has(v) })
      setTimes((s) => toggle(s, v))
    },
    setGroup,
    setSortMode,
    toggleSortDir,
    clearAll: () => {
      trackChallengeBrowse('challenges:filter:clear', { hadFilters: activeFilterCount > 0 })
      setQuery(''); setDifficulties(new Set()); setWeapons(new Set()); setStatuses(new Set()); setTimes(new Set())
    },
    // Resume strip "Show all": wipe every facet, then pin the status filter to
    // in-progress (Ongoing) only.
    showOnlyInProgress: () => {
      trackChallengeBrowse('challenges:filter:resume_in_progress')
      setQuery('')
      setDifficulties(new Set())
      setWeapons(new Set())
      setTimes(new Set())
      setStatuses(new Set([ChallengeService.AvailableChallengeStatusEnum.Ongoing]))
    },
    groups,
    visibleOrder,
    facetCounts,
    activeFilterCount,
    identity,
  }
}
